<template>
    <div class="sip-update-container">
        <div v-if="gathering" class="loading-container">
            <div class="spinner"></div>
        </div>
        <div v-else class="content">
            <div class="header">
                <h4 class="title">Аккаунты без sip данных</h4>
                <p class="subtitle">Добавьте или удалите аккаунты в таблице</p>
                <div class="address-filter">
                    <div class="filter-header">
                        <span class="filter-title">Адреса</span>
                        <div class="filter-actions">
                            <button class="filter-action-btn" @click="selectAllAddresses" :disabled="allSelected">Все</button>
                            <button class="filter-action-btn" @click="clearAllAddresses" :disabled="noneSelected">Очистить</button>
                            <button class="filter-action-btn btn-export" @click="exportExistingSipAsCsv" :disabled="noneSelected" title="Экспорт аккаунтов с SIP номерами">
                                📥 Экспорт SIP
                            </button>
                        </div>
                    </div>
                    <div class="address-chips">
                        <button 
                            v-for="(item, index) in allStreetAndBuildings" 
                            :key="index" 
                            class="address-chip"
                            :class="{ active: selectedStreetAndBuildings.includes(item.key) }"
                            @click="toggleAddress(item.key)"
                        >
                            <span class="chip-text">{{ item.key }}</span>
                            <span class="chip-count">{{ item.count }}</span>
                        </button>
                    </div>
                </div>
            </div>
            <div class="input-group">
                    <label for="startingSipNumber" class="input-label">Начальный sip номер</label>
                    <input type="text" class="text-input" v-model="startingSipNumber" placeholder="Введите номер" id="startingSipNumber" />
            </div>
            <div class="table-container">
                <table class="accounts-table">
                    <thead>
                        <tr>
                            <th class="col-number">#</th>
                            <th class="col-account">Аккаунт</th>
                            <th class="col-apartment">Квартира</th>
                            <th class="col-actions">
                                <button class="btn-icon btn-add" @click="addRow" title="Добавить строку">
                                    ➕
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, index) in editableRows" :key="row.id" :class="['row-' + row.status]">
                            <td class="cell-number">{{ index + 1 }}</td>
                            <td class="cell-account">
                                <span class="account-input">
                                    {{ row.username }}
                                </span>
                            </td>
                            <td class="cell-apartment">
                                <span v-if="row.accountData" class="apartment-value">
                                    {{ row.accountData.address?.street }} {{ row.accountData.address?.houseNum }} {{ row.accountData.address?.apartmentNum || '-' }}
                                </span>
                                <span v-else class="apartment-empty">-</span>
                            </td>
                            <td class="cell-actions">
                                <button class="btn-icon btn-delete" @click="removeRow(index)" title="Удалить строку">
                                    🗑️
                                </button>
                                <button class="btn-icon btn-edit" @click="showEditUser(row.username)" title="Редактировать пользователя">
                                    🖍
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="footer">
                <button class="btn btn-primary" @click="updateSipData" :disabled="!hasValidRows">
                    Обновить sip данные ({{ validRowsCount }})
                </button>
                <span class="stats">
                    Всего: {{ editableRows.length }} | Валидных: {{ validRowsCount }}
                </span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue'
import { onMounted } from 'vue'
import { ApiSys } from "@/services/api"
import UserEdit from './UserEdit.vue'

const accounts = ref([])
const { showModal, hideModal } = inject('modal')
const editableRows = ref([])
const selectedStreetAndBuildings = ref([])
const startingSipNumber = ref(10000)
let rowIdCounter = 0

const filteredSortedAccounts = computed(() => {
    return accounts.value.filter(account => account.sipUsername === null).sort((a, b) => a.user.localeCompare(b.user))
})

const allStreetAndBuildings = computed(() => {
    // Returns array of {key: string, count: number, street: string, building: string}
    const streetAndBuildingCount = {};
    filteredSortedAccounts.value.forEach(account => {
        if (!account.address?.street || !account.address?.houseNum) {
            return;
        }
        const streetAndBuilding = `${account.address.street.trim()} ${account.address.houseNum.trim()}`;
        if (!streetAndBuildingCount[streetAndBuilding]) {
            streetAndBuildingCount[streetAndBuilding] = {
                key: streetAndBuilding,
                count: 0,
                street: account.address.street.trim().toLowerCase(),
                building: account.address.houseNum.trim().toLowerCase()
            };
        }
        streetAndBuildingCount[streetAndBuilding].count++;
    });
    return Object.values(streetAndBuildingCount).sort((a, b) => b.count - a.count);
});

const filteredBySelectedStreetAndBuildings = computed(() => {
    if (selectedStreetAndBuildings.value.length === 0) {
        return filteredSortedAccounts.value;
    }
    return filteredSortedAccounts.value.filter(account => {
        if (!account.address?.street || !account.address?.houseNum) {
            return false;
        }
        const streetAndBuilding = `${account.address.street.trim()} ${account.address.houseNum.trim()}`;
        return selectedStreetAndBuildings.value.includes(streetAndBuilding);
    });
});

const displayAccounts = computed(() => {
    if (selectedStreetAndBuildings.value.length > 0) {
        return filteredBySelectedStreetAndBuildings.value
    }
    return filteredSortedAccounts.value
});

// Address filter helpers
const allSelected = computed(() => {
    return selectedStreetAndBuildings.value.length === allStreetAndBuildings.value.length
});

const noneSelected = computed(() => {
    return selectedStreetAndBuildings.value.length === 0
});

const toggleAddress = (key) => {
    const index = selectedStreetAndBuildings.value.indexOf(key);
    if (index === -1) {
        selectedStreetAndBuildings.value.push(key);
    } else {
        selectedStreetAndBuildings.value.splice(index, 1);
    }
    loadAllAccounts();
};

const selectAllAddresses = () => {
    selectedStreetAndBuildings.value = allStreetAndBuildings.value.map(item => item.key);
    loadAllAccounts();
};

const clearAllAddresses = () => {
    selectedStreetAndBuildings.value = [];
    loadAllAccounts();
};

// Export accounts with existing SIP numbers as CSV
const exportExistingSipAsCsv = () => {
    // Filter accounts that have SIP numbers and match selected addresses
    const accountsWithSip = accounts.value.filter(account => {
        // Must have a SIP number
        if (!account.address?.apartmentNum) return false;
        if (!account.sipUsername) return false;
        return true;
    });
    console.log(accountsWithSip);
    // Sort by apartment number
    accountsWithSip.sort((a, b) => {
        const aptA = parseInt(a.address?.apartmentNum, 10) || 0;
        const aptB = parseInt(b.address?.apartmentNum, 10) || 0;
        return aptA - aptB;
    });
    
    // Create CSV content
    const csvLines = ['apartment,sipNumber'];
    accountsWithSip.forEach(account => {
        const apartment = account.address?.apartmentNum || '';
        const sipNumber = account.sipUsername || '';
        csvLines.push(`${apartment},${sipNumber}`);
    });
    
    const csvContent = csvLines.join('\n');
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Generate filename based on selected addresses
    const addressPart = selectedStreetAndBuildings.value.length === 1 
        ? selectedStreetAndBuildings.value[0].replace(/\s+/g, '_') 
        : 'multiple_addresses';
    link.download = `sip_numbers_${addressPart}.csv`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const gathering = ref(true)
const error = ref(null)

const props = defineProps({
    division: {
        type: Object,
        required: true
    }
})

// Add a new empty row
const addRow = () => {
    editableRows.value.push({
        id: ++rowIdCounter,
        username: '',
        status: 'empty',
        accountData: null
    })
}

const showEditUser = (username) => {
    showModal(UserEdit, { division: props.division, username: username, hide: hideModal }, { "updateTree": () => { props.updateTree() } }, "Редактировать пользователя", "xl")
}

// Remove a row by index
const removeRow = (index) => {
    editableRows.value.splice(index, 1)
}
// Load all accounts from filteredSortedAccounts
const loadAllAccounts = () => {
    editableRows.value = displayAccounts.value.map(account => ({
        id: ++rowIdCounter,
        username: account.user,
        status: 'valid',
        accountData: account
    }))
}

// Count valid rows
const validRowsCount = computed(() => {
    return editableRows.value.filter(row => row.status === 'valid').length
})

const hasValidRows = computed(() => {
    return validRowsCount.value > 0
})

// Update sip data for valid accounts
const updateSipData = () => {
    const validAccounts = editableRows.value
        .filter(row => row.status === 'valid')
        .map(row => row.accountData)

    // Track how many times each apartment number has been seen
    const apartmentOccurrences = {};
    
    const sipData = validAccounts.map(account => {
        const apartmentNum = account.address?.apartmentNum || '0';
        const baseNum = parseInt(apartmentNum, 10) || 0;
        const startingNum = parseInt(startingSipNumber.value, 10);
        
        // Count occurrences of this apartment number
        apartmentOccurrences[apartmentNum] = (apartmentOccurrences[apartmentNum] || 0) + 1;
        const occurrence = apartmentOccurrences[apartmentNum];
        
        // Calculate base sip number: startingSip + apartmentNum
        const baseSip = String(startingNum + baseNum);
        
        let sipNumber;
        if (occurrence === 1) {
            // 1st occurrence: normal (e.g., 10253)
            sipNumber = baseSip;
        } else if (occurrence === 2) {
            // 2nd occurrence: remove second char, add "0" at end (e.g., 10253 -> 12530)
            sipNumber = baseSip[0] + baseSip.slice(2) + '0';
        } else {
            // 3rd+ occurrence: base result + "0" padding (e.g., 102530, 1025300, ...)
            sipNumber = baseSip + '0'.repeat(occurrence - 2);
        }
        
        return {
            username: account.user,
            sipNumber: sipNumber
        };
    });
    // public class UpdatedUser
    // {
    //     public string Username { get; set; }
    //     public string SipNumber { get; set; }
    // }

    // public class UpdateDivisionWithSipDto
    // {
    //     public List<UpdatedUser> Users { get; set; }
    //     public Guid DivisionUUID { get; set; }
    // }
    const data = {
        users: sipData,
        divisionUUID: props.division.id
    };
    console.log('SIP data to update:', data);
    ApiSys.updateUsersSipData(data);
}

onMounted(async () => {
    const result = await ApiSys.getFullUserList(props.division.id);
    if (result.error) {
        error.value = result.error
        return;
    }
    accounts.value = result
    gathering.value = false

    // Initialize with all street/building combinations selected
    selectedStreetAndBuildings.value = allStreetAndBuildings.value.map(item => item.key)

    // Initialize with all filtered accounts
    loadAllAccounts()

})
</script>

<style scoped>
.sip-update-container {
    color: #fff;
}

.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 20px;
}

.spinner {
    width: 48px;
    height: 48px;
    border: 3px solid rgba(255, 255, 255, 0.1);
    border-top-color: #8ba3c7;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.header {
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.title {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #8ba3c7;
}

.subtitle {
    margin: 0;
    font-size: 13px;
    color: #999;
}

.address-filter {
    margin-top: 16px;
}

.filter-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.filter-title {
    font-size: 12px;
    font-weight: 600;
    color: #8ba3c7;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.filter-actions {
    display: flex;
    gap: 8px;
}

.filter-action-btn {
    padding: 4px 10px;
    font-size: 11px;
    background: transparent;
    border: 1px solid rgba(139, 163, 199, 0.3);
    color: #8ba3c7;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
}

.filter-action-btn:hover:not(:disabled) {
    background: rgba(139, 163, 199, 0.15);
    border-color: rgba(139, 163, 199, 0.5);
}

.filter-action-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.filter-action-btn.btn-export {
    background: rgba(38, 198, 218, 0.15);
    border-color: rgba(38, 198, 218, 0.4);
    color: #26C6DA;
}

.filter-action-btn.btn-export:hover:not(:disabled) {
    background: rgba(38, 198, 218, 0.25);
    border-color: rgba(38, 198, 218, 0.6);
}

.address-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.address-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s;
    font-family: inherit;
}

.address-chip:hover {
    background: rgba(139, 163, 199, 0.1);
    border-color: rgba(139, 163, 199, 0.3);
}

.address-chip.active {
    background: rgba(139, 163, 199, 0.2);
    border-color: #8ba3c7;
}

.chip-text {
    font-size: 12px;
    color: #bbb;
}

.address-chip.active .chip-text {
    color: #fff;
}

.chip-count {
    font-size: 10px;
    font-weight: 600;
    color: #666;
    background: rgba(0, 0, 0, 0.3);
    padding: 2px 6px;
    border-radius: 10px;
    min-width: 20px;
    text-align: center;
}

.address-chip.active .chip-count {
    background: rgba(139, 163, 199, 0.4);
    color: #fff;
}

.input-group {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
}

.input-label {
    font-size: 16px;
    font-weight: 500;
    color: #8ba3c7;
    white-space: nowrap;
    min-width: fit-content;
}

.text-input {
    flex: 1;
    max-width: 300px;
    padding: 10px 14px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(139, 163, 199, 0.2);
    border-radius: 6px;
    color: #fff;
    font-size: 13px;
    max-width: 100px;
    font-family: inherit;
    transition: all 0.3s ease;
    outline: none;
}

.text-input:hover {
    border-color: rgba(139, 163, 199, 0.4);
    background: rgba(0, 0, 0, 0.4);
}

.text-input:focus {
    border-color: #8ba3c7;
    background: rgba(0, 0, 0, 0.5);
    box-shadow: 0 0 0 3px rgba(139, 163, 199, 0.15);
}

.text-input::placeholder {
    color: #666;
    font-style: italic;
}

.table-container {
    max-height: 500px;
    overflow-y: auto;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.2);
}

.table-container::-webkit-scrollbar {
    width: 8px;
}

.table-container::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
}

.table-container::-webkit-scrollbar-thumb {
    background: rgba(139, 163, 199, 0.3);
    border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
    background: rgba(139, 163, 199, 0.5);
}

.accounts-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.accounts-table thead {
    position: sticky;
    top: 0;
    background: #1a1a1a;
    z-index: 10;
}

.accounts-table th {
    padding: 12px 10px;
    text-align: left;
    font-weight: 600;
    color: #8ba3c7;
    border-bottom: 2px solid rgba(139, 163, 199, 0.3);
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.5px;
}

.col-number {
    width: 50px;
    text-align: center !important;
}

.col-account {
    width: auto;
}

.col-apartment {
    width: 120px;
}

.col-status {
    width: 130px;
}

.col-actions {
    width: 70px;
    text-align: center !important;
}

.accounts-table tbody tr {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    transition: background-color 0.2s;
}

.accounts-table tbody tr:hover {
    background: rgba(139, 163, 199, 0.05);
}

.accounts-table tbody tr.row-valid {
    background: rgba(38, 198, 218, 0.08);
}

.accounts-table tbody tr.row-invalid {
    background: rgba(252, 75, 108, 0.08);
}

.accounts-table tbody tr.row-duplicate {
    background: rgba(255, 178, 43, 0.08);
}

.accounts-table td {
    padding: 10px;
    color: #ddd;
}

.cell-number {
    text-align: center;
    color: #999;
    font-weight: 500;
}

.cell-account {
    padding: 8px 10px;
}

.account-input {
    width: 100%;
    padding: 6px 10px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    color: #fff;
    font-size: 13px;
    font-family: monospace;
    transition: all 0.2s;
}

.account-input:focus {
    outline: none;
    border-color: #8ba3c7;
    background: rgba(0, 0, 0, 0.4);
    box-shadow: 0 0 0 2px rgba(139, 163, 199, 0.1);
}

.account-input::placeholder {
    color: #666;
}

.cell-apartment {
    text-align: center;
}

.apartment-value {
    color: #8ba3c7;
    font-weight: 500;
}

.apartment-empty {
    color: #555;
}

.cell-status {
    padding: 8px 10px;
}

.status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.status-valid {
    background: rgba(38, 198, 218, 0.2);
    color: #26C6DA;
    border: 1px solid rgba(38, 198, 218, 0.3);
}

.status-invalid {
    background: rgba(252, 75, 108, 0.2);
    color: #fc4b6c;
    border: 1px solid rgba(252, 75, 108, 0.3);
}

.status-duplicate {
    background: rgba(255, 178, 43, 0.2);
    color: #ffb22b;
    border: 1px solid rgba(255, 178, 43, 0.3);
}

.status-empty {
    background: rgba(255, 255, 255, 0.05);
    color: #777;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.cell-actions {
    text-align: center;
}

.btn-icon {
    background: none;
    border: none;
    padding: 6px;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.2s;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
}

.btn-icon:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
}

.btn-add {
    opacity: 0.7;
}

.btn-add:hover {
    opacity: 1;
    background: rgba(38, 198, 218, 0.2);
}

.btn-delete:hover {
    background: rgba(252, 75, 108, 0.2);
}

.footer {
    display: flex;
    gap: 12px;
    align-items: center;
    padding-top: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.btn-primary {
    background: #8ba3c7;
    color: #1a1a1a;
}

.btn-primary:hover:not(:disabled) {
    background: #9bb3d7;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(139, 163, 199, 0.3);
}

.btn-primary:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.btn-secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #ddd;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-secondary:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
}

.stats {
    margin-left: auto;
    font-size: 12px;
    color: #999;
    font-weight: 500;
}
</style>